import { Inject, Injectable } from '@angular/core';
import { AppLauncher } from '@capacitor/app-launcher';
import { Device } from '@capacitor/device';
import { environment } from '../../environments/environment';
import { Settings } from '../data/models';
import { APP_SETTINGS } from '../data/settings-data.provider';

async function checkCanOpenUrl(url: string) {
  try {
    const { value } = await AppLauncher.canOpenUrl({ url });
    return value;
  } catch (err) {
    return false;
  }
}

async function openAppByUrl(url: string) {
  await AppLauncher.openUrl({ url });
}

export function navigateToExternalLink(url: string, openInNewTab = false) {
  if (openInNewTab) {
    window.open(url, '_blank');
    return;
  }

  window.location.href = url;
}

@Injectable({ providedIn: 'root' })
export class ExternalRoutesService {
  private readonly DOMAIN_KEY = '<subdomain>';

  get navigationToExternalUrlEnabled(): boolean {
    return !environment.runAsStandaloneApplication;
  }

  get adminEnabled(): boolean {
    return !environment.runOnAndroidPaymentTerminal;
  }

  constructor(@Inject(APP_SETTINGS) private readonly settings: Settings) {}

  async redirectToAdminPage() {
    const deviceInfo = await Device.getInfo();
    switch(deviceInfo.platform) {
      case 'android':
        return this.openAdminOnAndroid();
      case 'ios':
        return this.openAdminOnIos();
      case 'web':
        return this.openAdminInBrowser();
    }
  }

  private async openAdminOnAndroid() {
    const ADMIN_PACKAGE = 'de.versacommerce.versa';
    const PLAY_STORE_URL = 'https://play.google.com/store/apps/details';
    const haveAppInstalled = await checkCanOpenUrl(ADMIN_PACKAGE);
    if (haveAppInstalled) {
      openAppByUrl(ADMIN_PACKAGE);
    } else {
      const url = PLAY_STORE_URL + '?id=' + ADMIN_PACKAGE;
      navigateToExternalLink(url, true);
    }
  }

  private async openAdminOnIos() {
    const ADMIN_SCHEMA = 'versaapp://';
    const ADMIN_APP_ID = 'id1608316343';
    const APP_STORE_URL = 'itms-apps://apps.apple.com/de/app/versaapp/';
    const haveAppInstalled = await checkCanOpenUrl(ADMIN_SCHEMA);
    if (haveAppInstalled) {
      openAppByUrl(ADMIN_SCHEMA);
    } else {
      const url = APP_STORE_URL + ADMIN_APP_ID;
      navigateToExternalLink(url, true);
    }
  }

  private openAdminInBrowser() {
    navigateToExternalLink(this.prepareUrl(environment.adminSite));
  }

  openPinChangePageInNewTab() {
    navigateToExternalLink(this.prepareUrl(environment.changePinUrl), true);
  }

  private prepareUrl(url: string) {
    return url.replace(this.DOMAIN_KEY, this.settings.subdomain);
  }
}
